import { gql } from '@apollo/client';

// --- Command Execution Queries ---

// Returns paginated command execution history for devices.
// Used by ExecuteCommandDialog to display command status/results.
export const COMMAND_EXECUTIONS_QUERY = gql`
  query CommandExecutions(
    $first: Int
    $after: String
    $query: QueryCommandExecutionInput
    $sort: CommandExecutionSort
  ) {
    commandExecutions(first: $first, after: $after, query: $query, sort: $sort) {
      page {
        edges {
          cursor
          node {
            _id
            UUID
            deviceId
            deviceName
            command
            value
            status
            result
            createdAt
            lastUpdatedDate
            type
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

// --- MDM Queries ---

// Returns device info, WiFi networks, timezone, or app mode depending on command.
// `command` is a MDM_DETAILS_COMMANDS enum: GET_DEVICE_INFO, GET_APP_MODE, GET_TIMEZONE, GET_WIFI
// `fresh: true` bypasses the server's 15s GET_DEVICE_INFO throttle window (which
// otherwise serves the Redis mdmreported cache) — used by the Settings tab's
// post-save confirm pull, which must read the device's real post-apply state.
export const MDM_DETAILS = gql`
  query MdmDetails($id: String!, $teamId: String, $command: MDM_DETAILS_COMMANDS, $fresh: Boolean) {
    mdmDetails(_id: $id, teamId: $teamId, command: $command, fresh: $fresh)
  }
`;

// Returns available Android OptiSigns app versions for OTA install
export const ANDROID_OPTISIGNS_VERSIONS = gql`
  mutation AndroidOptiSignsVersions {
    androidOptiSignsVersions
  }
`;

// OptiStick 5 (Gen5) player versions — the server reads the prd app-channel versions.json.
// No url is returned: the device resolves the artifact itself from the version string.
export const OPTISTICK5_PLAYER_VERSIONS = gql`
  query OptiStick5PlayerVersions($teamId: String, $deviceId: String!) {
    optiStick5PlayerVersions(teamId: $teamId, deviceId: $deviceId) {
      version
      mediad
      disabledReason
    }
  }
`;

export interface OptiStick5PlayerVersionItem {
  version: string;
  /** Linked mediad the device installs first when its running one is older */
  mediad?: string | null;
  /** Why the device would refuse this version, e.g. 'needs_newer_os' */
  disabledReason?: string | null;
}

export interface OptiStick5PlayerVersionsResult {
  optiStick5PlayerVersions: OptiStick5PlayerVersionItem[];
}

// --- MDM Mutations ---

// Issues MDM commands: SET_TIMEZONE, SET_KIOSK_APP, ADD_WIFI_AP, REMOVE_WIFI_AP, etc.
export const MDM_COMMAND = gql`
  mutation MdmCommand($teamId: String, $payload: MdmCommandInput!) {
    mdmCommand(teamId: $teamId, payload: $payload)
  }
`;

// Adds a single command execution entry (for device-specific commands like RS232, install, etc.)
export const ADD_COMMAND_EXECUTION = gql`
  mutation AddCommandExecution($teamId: String, $payload: CommandExecutionInput!) {
    addCommandExecution(teamId: $teamId, payload: $payload)
  }
`;

// Adds command execution for multiple devices/groups
export const ADD_COMMAND_EXECUTION_GROUP = gql`
  mutation AddCommandExecutionGroup($teamId: String, $payload: CommandExecutionGroupInput!) {
    addCommandExecutionGroup(teamId: $teamId, payload: $payload)
  }
`;
